import { useQuery } from "@tanstack/react-query";
import {
  ExternalLinkIcon,
  FileArchiveIcon,
  FileTextIcon,
  GavelIcon,
  GlobeIcon,
  ImageIcon,
  LandmarkIcon,
  ListTreeIcon,
  SearchIcon,
  UploadIcon,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import type { Source, Turn } from "../../../../shared/api.ts";
import { EmptyState, ErrorState } from "@/components/common/states.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { TabCount } from "@/components/ui/tabs.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { groupSources } from "@/lib/turns.ts";
import { cn, domainOf, openFile } from "@/lib/utils.ts";

const KIND_ICONS: Record<Source["kind"], LucideIcon> = {
  product: GlobeIcon,
  decision: GavelIcon,
  bti: LandmarkIcon,
  tariff: ListTreeIcon,
  search: SearchIcon,
  image: ImageIcon,
  upload: UploadIcon,
  other: FileArchiveIcon,
};

function SavedCopyDialog({ source, onClose }: { source: Source | null; onClose: () => void }) {
  const { t, fmt } = useI18n();
  const documentId = source?.document?.id ?? null;
  const query = useQuery({
    queryKey: queryKeys.document(documentId ?? ""),
    queryFn: () => api.document(documentId ?? ""),
    enabled: documentId !== null,
    staleTime: Infinity,
  });
  const doc = query.data;
  return (
    <Dialog open={source !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent closeLabel={t.common.close} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="line-clamp-2">{doc?.title ?? source?.title ?? t.sources.savedCopy}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{t.sources.savedCopy}</span>
            {doc?.fetchedAt ? <span>{t.sources.savedAt(fmt.dateTime(doc.fetchedAt))}</span> : null}
            {doc?.url || source?.url ? (
              <a
                href={doc?.url ?? source?.url ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
              >
                {t.sources.openOriginal}
                <ExternalLinkIcon className="size-3" aria-hidden />
              </a>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60dvh] min-h-40 overflow-y-auto rounded-lg border bg-muted/40 p-4">
          {query.isPending ? (
            <div className="flex flex-col gap-2">
              {[92, 100, 84, 96, 70].map((width) => (
                <Skeleton key={width} className="h-3.5" style={{ width: `${width}%` }} />
              ))}
            </div>
          ) : query.isError ? (
            <ErrorState error={query.error} onRetry={() => void query.refetch()} title={t.sources.docError} className="py-4" />
          ) : doc?.text ? (
            <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-foreground/90">{doc.text}</p>
          ) : (
            <p className="text-[13px] text-muted-foreground">{t.sources.noText}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Excerpt({ text }: { text: string }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const long = text.length > 140;
  return (
    <div className="text-[12.5px] leading-relaxed text-muted-foreground">
      <p className={cn(!expanded && "line-clamp-2")}>{text}</p>
      {long ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-0.5 text-[12px] font-semibold text-primary hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:outline-none"
        >
          {expanded ? t.common.showLess : t.common.showMore}
        </button>
      ) : null}
    </div>
  );
}

function SourceItem({ source, onSavedCopy }: { source: Source; onSavedCopy: (source: Source) => void }) {
  const { t } = useI18n();
  const Icon = source.document?.kind === "pdf" ? FileTextIcon : KIND_ICONS[source.kind];
  const domain = domainOf(source.url);
  const title = source.title || (source.kind === "search" ? source.query : null) || domain || t.sources.noTitle;
  const doc = source.document;
  const opensRaw = doc && (doc.kind === "pdf" || doc.kind === "image" || doc.kind === "file") && doc.rawUrl;
  return (
    <li className="flex gap-3 px-4 py-3">
      <span
        className={cn(
          "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg border bg-card text-muted-foreground",
          source.status !== "ok" && "border-danger-border bg-danger-soft text-danger",
        )}
      >
        <Icon className="size-3.5" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-start gap-2">
          {source.url ? (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 text-[13px] leading-snug font-semibold text-foreground hover:text-primary hover:underline"
            >
              <span className="line-clamp-2 break-words">{title}</span>
            </a>
          ) : (
            <span className="line-clamp-2 min-w-0 flex-1 text-[13px] leading-snug font-semibold break-words">{title}</span>
          )}
          {source.status === "failed" ? <Badge variant="danger">{t.sources.failed}</Badge> : null}
          {source.status === "blocked" ? <Badge variant="warning">{t.sources.blocked}</Badge> : null}
        </div>
        {domain && source.kind !== "search" ? <span className="truncate text-[11.5px] text-muted-foreground">{domain}</span> : null}
        {source.query && source.kind === "search" && source.title ? (
          <span className="code-mono truncate text-[11.5px] text-muted-foreground">
            {t.sources.query}: {source.query}
          </span>
        ) : null}
        {source.excerpt ? <Excerpt text={source.excerpt} /> : null}
        {source.error ? <p className="text-[12px] text-danger">{source.error}</p> : null}
        {doc ? (
          <Button
            variant="link"
            size="xs"
            className="h-6 self-start text-[12px]"
            onClick={() => (opensRaw && doc.rawUrl ? openFile(doc.rawUrl) : onSavedCopy(source))}
          >
            {t.sources.savedCopy}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/** Everything the agent searched or opened, grouped by kind, newest turn first. */
export function SourcesTab({ sources, turns }: { sources: Source[]; turns: Turn[] }) {
  const { t } = useI18n();
  const [copy, setCopy] = useState<Source | null>(null);
  if (sources.length === 0) {
    return <EmptyState icon={SearchIcon} text={t.sources.empty} className="py-14" />;
  }
  const groups = groupSources(sources, turns);
  return (
    <div className="pb-4">
      {groups.map((group) => (
        <section key={group.kind} aria-label={t.sources.groups[group.kind]}>
          <h3 className="sticky top-0 z-10 flex items-center gap-2 border-b bg-panel/95 px-4 py-2 text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase backdrop-blur">
            {t.sources.groups[group.kind]}
            <TabCount value={group.sources.length} />
          </h3>
          <ul className="divide-y">
            {group.sources.map((source) => (
              <SourceItem key={source.id} source={source} onSavedCopy={setCopy} />
            ))}
          </ul>
        </section>
      ))}
      <SavedCopyDialog source={copy} onClose={() => setCopy(null)} />
    </div>
  );
}
